// V17.299: the app's root route gate, extracted from
// ARResidentialTeamBoardRoute.tsx so that neither board's route file owns the
// other board's escape hatch.
//
// WHY THIS EXISTS (V17.262 Blocker 1): a paired 55" TV has no session. Its
// access token expired months ago and there is no refresh flow.
// `<ProtectedRoute>` early-returns `<Navigate to="/login">` whenever
// `!isAuthenticated`, and App.tsx wraps EVERY route in
// `<ProtectedRoute><Layout /></ProtectedRoute>` at `path="/"`. That outer gate
// runs before React Router descends to any leaf, so a TV pointed at a board
// URL always landed on the login page.
//
// Fix: read `?tv=1` OUTSIDE the session gate. A registered TV path with
// `?tv=1` renders its wall-display component directly — full-screen, no app
// chrome, unauthenticated. Everything else falls through to the original,
// unchanged gating.
//
// This is safe because the TV components perform their OWN auth: each fetches
// through its display-token endpoint and renders a full-screen "DISPLAY
// DISCONNECTED" state on 401/403. Skipping the frontend session gate does not
// skip backend auth; it only lets the TV reach the component that does it.

import { useLocation } from 'react-router-dom';
import ProtectedRoute from '../../components/common/ProtectedRoute';
import Layout from '../../components/layout/Layout';
import ARCommercialTeamBoardTV from './ARCommercialTeamBoardTV';
import ARResidentialTeamBoardTV from './ARResidentialTeamBoardTV';

interface TvRoute {
  path: string;
  element: () => JSX.Element;
}

/**
 * Every path that may render a wall display without a session.
 *
 * Adding an entry here widens the app's only unauthenticated surface, so each
 * one must render a component that authenticates itself against a scoped
 * display-token endpoint.
 */
export const TV_ROUTES: ReadonlyArray<TvRoute> = [
  { path: '/ar/residential/team-board', element: ARResidentialTeamBoardTV },
  { path: '/ar/commercial/team-board', element: ARCommercialTeamBoardTV },
];

/**
 * Exact match after normalising a trailing slash. React Router matches
 * `/team-board/` to the `/team-board` route but `location.pathname` keeps the
 * slash, so a strict compare would send the TV to a login screen. This URL is
 * typed into a television by hand, and "shows the login page" is an expensive
 * thing to diagnose from across an ops room.
 *
 * Exact, never `startsWith`: a prefix match would open the escape hatch on
 * every path that merely begins with a board's path.
 */
export function matchTvRoute(pathname: string, search: string): TvRoute | null {
  if (new URLSearchParams(search).get('tv') !== '1') return null;
  const normalised = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return TV_ROUTES.find((route) => route.path === normalised) ?? null;
}

/**
 * Root-level gate. Replaces the plain `<ProtectedRoute><Layout /></ProtectedRoute>`
 * that App.tsx uses as the element for its top-level `path="/"` route — the
 * ancestor of every other route via nested routing.
 */
export function AppRootGate() {
  const location = useLocation();

  const tv = matchTvRoute(location.pathname, location.search);
  if (tv) {
    const Display = tv.element;
    return <Display />;
  }

  return (
    <ProtectedRoute>
      <Layout />
    </ProtectedRoute>
  );
}

export default AppRootGate;
